import type {PropertyTarget, PropertyValuesRecord, TargetLiterals} from '@github-ui/custom-properties-types'
import {human} from '@github-ui/formatters'
import {ListView} from '@github-ui/list-view'
import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {PencilIcon} from '@primer/octicons-react'
import {ActionList, Button, IconButton, Spinner, Truncate} from '@primer/react'
import {type ComponentProps, type ReactNode, useCallback, useMemo} from 'react'

import {ListEmptyState} from './ListEmptyState'
import styles from './TargetsList.module.css'

const EDIT_MULTIPLE_TARGETS_BUTTON_ID = 'button:edit-multiple-targets'

interface Props<T extends PropertyTarget> {
  title: string
  selectedTargetIds: Set<number>
  totalCount: number
  targets: T[]
  targetLiterals: TargetLiterals
  isLoading?: boolean
  onSelectionChange: (selectedIds: Set<number>) => void
  onEditTargetPropertiesClick: (selection: T[], elementId: string) => void
  renderItem: (target: T, props: Pick<ItemProps, 'selected' | 'onSelect' | 'onEditButtonClick'>) => ReactNode
  renderEmptyState?: () => ReactNode
}

export function TargetsList<T extends PropertyTarget>({
  title,
  targetLiterals,
  selectedTargetIds,
  totalCount,
  targets,
  isLoading,
  onSelectionChange,
  onEditTargetPropertiesClick,
  renderEmptyState,
  renderItem,
}: Props<T>) {
  const onTargetSelectionToggle = useCallback(
    (target: T) => {
      const update = new Set(selectedTargetIds)
      if (selectedTargetIds.has(target.id)) {
        update.delete(target.id)
        onSelectionChange(update)
      } else {
        update.add(target.id)
        onSelectionChange(update)
      }
    },
    [selectedTargetIds, onSelectionChange],
  )

  const selectedTargets = useMemo(
    () => targets.filter(target => selectedTargetIds.has(target.id)),
    [targets, selectedTargetIds],
  )
  const hasSelectedTargets = selectedTargets.length > 0

  if (!targets.length) {
    if (renderEmptyState) return renderEmptyState()
    return <ListEmptyState>No {targetLiterals.multiple} have been found</ListEmptyState>
  }

  const metadata = (
    <ListViewMetadata
      onToggleSelectAll={isSelectAllChecked => {
        return isSelectAllChecked
          ? onSelectionChange(new Set(targets.map(target => target.id)))
          : onSelectionChange(new Set())
      }}
      title={<span className="text-bold">{targetCountLabel(totalCount, targetLiterals)}</span>}
      actionsLabel="Actions"
      actions={[
        {
          key: 'edit',
          render(isOverflowMenu) {
            if (!hasSelectedTargets) {
              return <></>
            }

            return isOverflowMenu ? (
              <ActionList.Item
                id={EDIT_MULTIPLE_TARGETS_BUTTON_ID}
                onSelect={() => onEditTargetPropertiesClick(selectedTargets, EDIT_MULTIPLE_TARGETS_BUTTON_ID)}
              >
                <ActionList.LeadingVisual>
                  <PencilIcon />
                </ActionList.LeadingVisual>
                Edit properties
              </ActionList.Item>
            ) : (
              <Button
                id={EDIT_MULTIPLE_TARGETS_BUTTON_ID}
                leadingVisual={PencilIcon}
                onClick={() => onEditTargetPropertiesClick(selectedTargets, EDIT_MULTIPLE_TARGETS_BUTTON_ID)}
              >
                Edit properties
              </Button>
            )
          },
        },
      ]}
    >
      {isLoading && <Spinner size="small" className="mr-2" />}
    </ListViewMetadata>
  )

  return (
    <div data-testid="targets-properties-list" className={styles.listContainer}>
      <ListView
        selectedCount={selectedTargets.length}
        isSelectable
        variant="compact"
        metadata={metadata}
        title={title}
        titleHeaderTag="h2"
        singularUnits={targetLiterals.single}
        pluralUnits={targetLiterals.multiple}
        totalCount={targets.length}
      >
        {targets.map(target =>
          renderItem(target, {
            selected: selectedTargetIds.has(target.id),
            onSelect: () => onTargetSelectionToggle(target),
            onEditButtonClick: buttonId => onEditTargetPropertiesClick([target], buttonId),
          }),
        )}
      </ListView>
    </div>
  )
}

interface ItemProps {
  name: string
  description?: string | null
  properties?: PropertyValuesRecord
  leadingIcon?: ComponentProps<typeof ListItemLeadingVisual>['icon']
  /**
   * If provided makes item title a link.
   * Otherwise item title is rendered as non-interactive element.
   *
   */
  linkToTarget?: string
  /**
   * Id of the clicked edit button.
   * Can be used to restore focus after editing.
   */
  onEditButtonClick: (editButtonId: string) => void
  selected: boolean
  onSelect: () => void
}

function TargetListItem({
  name,
  description,
  leadingIcon,
  properties,
  selected,
  linkToTarget,
  onEditButtonClick,
  onSelect,
}: ItemProps) {
  const buttonId = `list-item:button:edit-target-${name}`
  const propertiesLength = Object.keys(properties || {}).length

  return (
    <ListItem
      title={<ListItemTitle headingClassName={styles.listItemHeading} href={linkToTarget} value={name} />}
      onSelect={onSelect}
      isSelected={selected}
      metadata={
        <>
          <ListItemMetadata alignment="right" className="pr-0 pl-2">
            <span>
              {propertiesLength
                ? `${propertiesLength} ${propertiesLength === 1 ? 'property' : 'properties'}`
                : 'No properties'}
            </span>
          </ListItemMetadata>
          <ListItemMetadata alignment="right" variant="primary" className="p-0">
            <IconButton
              id={buttonId}
              icon={PencilIcon}
              variant="invisible"
              aria-label={`Edit ${name}'s properties`}
              onClick={() => onEditButtonClick(buttonId)}
              description="Edit properties"
            />
          </ListItemMetadata>
        </>
      }
      metadataContainerClassName="pr-3"
    >
      {leadingIcon && (
        <ListItemLeadingContent>
          <ListItemLeadingVisual color="fg.muted" icon={leadingIcon} />
        </ListItemLeadingContent>
      )}
      <ListItemMainContent>
        <ListItemDescription className={styles.listItemDescription}>
          {description && (
            <Truncate inline title={description} className={styles.listItemDescriptionTruncate}>
              {description}
            </Truncate>
          )}
        </ListItemDescription>
      </ListItemMainContent>
    </ListItem>
  )
}

TargetsList.Item = TargetListItem

function targetCountLabel(count: number, literals: TargetLiterals) {
  return `${human(count)} ${count === 1 ? literals.single : literals.multiple}`
}
